/**
 * Product Variants Editor Component
 *
 * Allows creating product options (like Size, Color) and generating
 * variant combinations with individual pricing, SKU, and inventory.
 */

import { useState, useEffect, useCallback } from 'react';

export interface ProductOption {
  id: string;
  name: string;
  values: string[];
}

export interface ProductVariant {
  id: string;
  options: Record<string, string>; // { "Size": "Large", "Color": "Red" }
  price: number;
  compareAtPrice?: number;
  sku?: string;
  inventoryQuantity: number;
  enabled: boolean;
}

interface VariantsEditorProps {
  options: ProductOption[];
  variants: ProductVariant[];
  basePrice: number;
  currency?: string;
  onChange: (options: ProductOption[], variants: ProductVariant[]) => void;
}

export default function VariantsEditor({
  options,
  variants,
  basePrice,
  onChange,
}: VariantsEditorProps) {
  const [showAddOption, setShowAddOption] = useState(false);
  const [newOptionName, setNewOptionName] = useState('');
  const [newOptionValues, setNewOptionValues] = useState('');
  const [editingOption, setEditingOption] = useState<string | null>(null);
  const [editOptionName, setEditOptionName] = useState('');
  const [editOptionValues, setEditOptionValues] = useState('');

  // Generate all variant combinations from options
  const generateVariants = useCallback((opts: ProductOption[]): ProductVariant[] => {
    if (opts.length === 0) return [];

    const combinations: Record<string, string>[][] = [[]];

    for (const option of opts) {
      const newCombinations: Record<string, string>[][] = [];
      for (const combo of combinations) {
        for (const value of option.values) {
          newCombinations.push([...combo, { [option.name]: value }]);
        }
      }
      combinations.length = 0;
      combinations.push(...newCombinations);
    }

    return combinations.map((combo) => {
      const optionsObj = combo.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      const variantId = Object.values(optionsObj).join('-').toLowerCase().replace(/\s+/g, '-');

      // Check if this variant already exists
      const existingVariant = variants.find(v => {
        const existingKey = Object.entries(v.options).sort().map(([k, v]) => `${k}:${v}`).join('|');
        const newKey = Object.entries(optionsObj).sort().map(([k, v]) => `${k}:${v}`).join('|');
        return existingKey === newKey;
      });

      if (existingVariant) {
        return existingVariant;
      }

      return {
        id: variantId,
        options: optionsObj,
        price: basePrice,
        sku: '',
        inventoryQuantity: 0,
        enabled: true,
      };
    });
  }, [variants, basePrice]);

  // Regenerate variants when options change
  useEffect(() => {
    if (options.length > 0) {
      const newVariants = generateVariants(options);
      if (JSON.stringify(newVariants.map(v => v.options)) !== JSON.stringify(variants.map(v => v.options))) {
        onChange(options, newVariants);
      }
    }
  }, [options]);

  const handleAddOption = () => {
    if (!newOptionName.trim() || !newOptionValues.trim()) return;

    const values = newOptionValues
      .split(',')
      .map(v => v.trim())
      .filter(v => v.length > 0);

    if (values.length === 0) return;

    const newOption: ProductOption = {
      id: crypto.randomUUID(),
      name: newOptionName.trim(),
      values,
    };

    const newOptions = [...options, newOption];
    const newVariants = generateVariants(newOptions);
    onChange(newOptions, newVariants);

    setNewOptionName('');
    setNewOptionValues('');
    setShowAddOption(false);
  };

  const handleUpdateOption = (optionId: string) => {
    if (!editOptionName.trim() || !editOptionValues.trim()) return;

    const values = editOptionValues
      .split(',')
      .map(v => v.trim())
      .filter(v => v.length > 0);

    if (values.length === 0) return;

    const newOptions = options.map(opt =>
      opt.id === optionId
        ? { ...opt, name: editOptionName.trim(), values }
        : opt
    );

    const newVariants = generateVariants(newOptions);
    onChange(newOptions, newVariants);

    setEditingOption(null);
    setEditOptionName('');
    setEditOptionValues('');
  };

  const handleDeleteOption = (optionId: string) => {
    const newOptions = options.filter(opt => opt.id !== optionId);
    const newVariants = newOptions.length > 0 ? generateVariants(newOptions) : [];
    onChange(newOptions, newVariants);
  };

  const handleUpdateVariant = (variantId: string, updates: Partial<ProductVariant>) => {
    const newVariants = variants.map(v =>
      v.id === variantId ? { ...v, ...updates } : v
    );
    onChange(options, newVariants);
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toFixed(2);
  };

  const parsePrice = (value: string) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : Math.round(num * 100);
  };

  const getVariantName = (variant: ProductVariant) => {
    return Object.values(variant.options).join(' / ');
  };

  const enabledVariantsCount = variants.filter(v => v.enabled).length;

  return (
    <div className="space-y-6">
      {/* Options Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Product Options
          </h3>
          {options.length > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {options.length} option{options.length !== 1 ? 's' : ''}, {variants.length} variant{variants.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Existing Options */}
        <div className="space-y-3">
          {options.map((option) => (
            <div
              key={option.id}
              className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4"
            >
              {editingOption === option.id ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={editOptionName}
                    onChange={(e) => setEditOptionName(e.target.value)}
                    placeholder="Option name (e.g., Size)"
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="text"
                    value={editOptionValues}
                    onChange={(e) => setEditOptionValues(e.target.value)}
                    placeholder="Values (comma-separated, e.g., Small, Medium, Large)"
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingOption(null)}
                      className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUpdateOption(option.id)}
                      className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {option.name}
                    </h4>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {option.values.map((value, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded border border-gray-200 dark:border-gray-500"
                        >
                          {value}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingOption(option.id);
                        setEditOptionName(option.name);
                        setEditOptionValues(option.values.join(', '));
                      }}
                      className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteOption(option.id)}
                      className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add Option Form */}
        {showAddOption ? (
          <div className="mt-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
            <input
              type="text"
              value={newOptionName}
              onChange={(e) => setNewOptionName(e.target.value)}
              placeholder="Option name (e.g., Size, Color)"
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              autoFocus
            />
            <input
              type="text"
              value={newOptionValues}
              onChange={(e) => setNewOptionValues(e.target.value)}
              placeholder="Values (comma-separated, e.g., Small, Medium, Large)"
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddOption();
              }}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowAddOption(false);
                  setNewOptionName('');
                  setNewOptionValues('');
                }}
                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddOption}
                disabled={!newOptionName.trim() || !newOptionValues.trim()}
                className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Option
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowAddOption(true)}
            className="mt-3 w-full py-2 text-sm font-medium text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 border border-dashed border-teal-300 dark:border-teal-700 rounded-lg transition-colors"
          >
            + Add Option (e.g., Size, Color)
          </button>
        )}
      </div>

      {/* Variants Section */}
      {variants.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Variant Pricing & Inventory
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {enabledVariantsCount} of {variants.length} enabled
            </span>
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">
                      Variant
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">
                      Price
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">
                      Compare At
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">
                      SKU
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">
                      Qty
                    </th>
                    <th className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300 w-16">
                      On
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {variants.map((variant) => (
                    <tr
                      key={variant.id}
                      className={`${
                        variant.enabled
                          ? 'bg-white dark:bg-gray-800'
                          : 'bg-gray-50 dark:bg-gray-800/50 opacity-60'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {getVariantName(variant)}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input
                            type="number"
                            value={formatPrice(variant.price)}
                            onChange={(e) => handleUpdateVariant(variant.id, { price: parsePrice(e.target.value) })}
                            step="0.01"
                            min="0"
                            className="w-24 pl-5 pr-2 py-1.5 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-1 focus:ring-teal-500 focus:border-transparent"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input
                            type="number"
                            value={variant.compareAtPrice ? formatPrice(variant.compareAtPrice) : ''}
                            onChange={(e) => handleUpdateVariant(variant.id, {
                              compareAtPrice: e.target.value ? parsePrice(e.target.value) : undefined
                            })}
                            step="0.01"
                            min="0"
                            placeholder="—"
                            className="w-24 pl-5 pr-2 py-1.5 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-1 focus:ring-teal-500 focus:border-transparent"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={variant.sku || ''}
                          onChange={(e) => handleUpdateVariant(variant.id, { sku: e.target.value })}
                          placeholder="SKU"
                          className="w-28 px-2 py-1.5 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-1 focus:ring-teal-500 focus:border-transparent"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={variant.inventoryQuantity}
                          onChange={(e) => handleUpdateVariant(variant.id, {
                            inventoryQuantity: parseInt(e.target.value) || 0
                          })}
                          min="0"
                          className="w-20 px-2 py-1.5 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-1 focus:ring-teal-500 focus:border-transparent"
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleUpdateVariant(variant.id, { enabled: !variant.enabled })}
                          className={`w-10 h-5 rounded-full transition-colors ${
                            variant.enabled
                              ? 'bg-teal-600'
                              : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`block w-4 h-4 bg-white rounded-full shadow transform transition-transform ${
                              variant.enabled ? 'translate-x-5' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bulk Actions */}
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                const newVariants = variants.map(v => ({ ...v, price: basePrice }));
                onChange(options, newVariants);
              }}
              className="text-xs text-teal-600 dark:text-teal-400 hover:underline"
            >
              Reset all prices to base (${formatPrice(basePrice)})
            </button>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <button
              type="button"
              onClick={() => {
                const newVariants = variants.map(v => ({ ...v, enabled: true }));
                onChange(options, newVariants);
              }}
              className="text-xs text-teal-600 dark:text-teal-400 hover:underline"
            >
              Enable all
            </button>
            <button
              type="button"
              onClick={() => {
                const newVariants = variants.map(v => ({ ...v, enabled: false }));
                onChange(options, newVariants);
              }}
              className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
            >
              Disable all
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {options.length === 0 && (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <p className="text-sm">Add options like Size or Color to create product variants</p>
          <p className="text-xs mt-1">Each variant can have its own price, SKU, and inventory</p>
        </div>
      )}
    </div>
  );
}
